/**
 * Razorpay Integration Demo Page
 * Test page for Razorpay payment integration
 */

import React, { useState } from 'react';
import { ArrowLeft, CreditCard, CheckCircle2, XCircle } from 'lucide-react';
import { RazorpayPaymentButton } from '../components/payment/RazorpayPaymentButton';
import { useNavigate } from 'react-router-dom';

interface Transaction {
  id: number;
  gaushalaName: string;
  gaushalaId: number;
  amount: number;
  description: string;
  status: 'pending' | 'paid' | 'failed';
}

export const RazorpayDemo: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([
    {
      id: 1001,
      gaushalaName: 'Shri Govind Gaushala',
      gaushalaId: 1,
      amount: 500.00,
      description: 'Dung collection payment - 50kg @ ₹10/kg',
      status: 'pending',
    },
    {
      id: 1002,
      gaushalaName: 'Desi Cow Protection Center',
      gaushalaId: 2,
      amount: 750.50,
      description: 'Dung collection payment - 75kg @ ₹10/kg',
      status: 'pending',
    },
    {
      id: 1003,
      gaushalaName: 'Gaushala Seva Sangh',
      gaushalaId: 3,
      amount: 1200.00,
      description: 'Dung collection payment - 120kg @ ₹10/kg',
      status: 'pending',
    },
  ]);

  const [paymentResults, setPaymentResults] = useState<Record<number, { success: boolean; message: string }>>({});

  const handlePaymentSuccess = (transactionId: number, paymentId: string, orderId: string) => {
    console.log('Payment successful:', { transactionId, paymentId, orderId });

    setTransactions(prev =>
      prev.map(txn =>
        txn.id === transactionId ? { ...txn, status: 'paid' as const } : txn
      )
    );

    setPaymentResults(prev => ({
      ...prev,
      [transactionId]: {
        success: true,
        message: `Payment successful! Payment ID: ${paymentId}`,
      },
    }));
  };

  const handlePaymentFailure = (transactionId: number, error: string) => {
    console.error('Payment failed:', { transactionId, error });

    setTransactions(prev =>
      prev.map(txn =>
        txn.id === transactionId ? { ...txn, status: 'failed' as const } : txn
      )
    );

    setPaymentResults(prev => ({
      ...prev,
      [transactionId]: {
        success: false,
        message: `Payment failed: ${error}`,
      },
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Razorpay Integration Demo</h1>
                <p className="text-sm text-gray-600">Test payment gateway integration</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Service Running on Port 8082
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <CreditCard className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">
                Razorpay Payment Integration Active
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                The Razorpay payment gateway is successfully integrated with the biogas service.
                Click on any "Pay Now" button below to test the payment flow.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-blue-600">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>Backend: http://localhost:8082/biogas-service</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>Test Mode: Using test credentials</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>API: /api/v1/payments/razorpay/*</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>Currency: INR (Indian Rupee)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Transactions
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Test transactions for payment processing
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {transactions.map((txn) => (
              <div key={txn.id} className="px-6 py-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-6">
                  {/* Transaction Details */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {txn.gaushalaName}
                      </h3>
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${txn.status === 'paid' ? 'bg-green-100 text-green-700' :
                          txn.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'}
                      `}>
                        {txn.status === 'paid' ? '✓ Paid' :
                         txn.status === 'failed' ? '✗ Failed' :
                         '⏳ Pending'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {txn.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Transaction ID: #{txn.id}</span>
                      <span>•</span>
                      <span>Gaushala ID: {txn.gaushalaId}</span>
                    </div>

                    {/* Payment Result */}
                    {paymentResults[txn.id] && (
                      <div className={`
                        mt-4 px-4 py-3 rounded-lg border text-sm
                        ${paymentResults[txn.id].success
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-red-50 border-red-200 text-red-700'}
                      `}>
                        {paymentResults[txn.id].success ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>{paymentResults[txn.id].message}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            <span>{paymentResults[txn.id].message}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Amount & Payment Button */}
                  <div className="flex flex-col items-end gap-3">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        ₹{txn.amount.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Amount Due
                      </div>
                    </div>

                    <RazorpayPaymentButton
                      transactionId={txn.id}
                      amount={txn.amount}
                      gaushalaName={txn.gaushalaName}
                      gaushalaId={txn.gaushalaId}
                      description={txn.description}
                      onSuccess={(paymentId, orderId) =>
                        handlePaymentSuccess(txn.id, paymentId, orderId)
                      }
                      onFailure={(error) => handlePaymentFailure(txn.id, error)}
                      disabled={txn.status === 'paid'}
                      className="min-w-[180px]"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documentation Link */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-lg border border-gray-200 shadow-sm">
            <span className="text-sm text-gray-600">
              For complete integration guide, see
            </span>
            <span className="text-sm font-medium text-green-600">
              RAZORPAY_INTEGRATION_GUIDE.md
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RazorpayDemo;
